/**
 * Delivery-attempt details shown on the order detail page: the out-for-delivery rider, and the location of
 * Delivered / Failed Delivery scans with Google Maps links.
 *
 * Sources on the order (written by the courier webhook pipeline; read-only here):
 *  - order_history[].additional.latitude/longitude — per scan, so every attempt keeps its own location
 *  - milestoneInfo.{outForDelivery|failedDelivery|delivered} — LATEST occurrence only, overwritten per event
 *  - feDetails — rider from Clickpost's separate field-executive update (fallback when milestoneInfo has none)
 */

export const OFD_CODE = 6
export const DELIVERED_CODE = 8
export const FAILED_DELIVERY_CODE = 9

/** Couriers send blanks, non-numbers and 0,0 — none of which is a real place. */
export function isValidCoord(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) return false
  if (String(lat).trim() === '' || String(lng).trim() === '') return false
  const a = Number(lat)
  const o = Number(lng)
  if (!Number.isFinite(a) || !Number.isFinite(o)) return false
  if (a === 0 && o === 0) return false
  return Math.abs(a) <= 90 && Math.abs(o) <= 180
}

/** The three Google Maps links from the requirement, in display order. */
export function mapLinks(lat, lng) {
  const ll = `${String(lat).trim()},${String(lng).trim()}`
  return [
    { label: 'Show Location', href: `https://www.google.com/maps?q=${ll}` },
    { label: 'Navigate', href: `https://www.google.com/maps/dir/?api=1&destination=${ll}` },
    { label: 'Show Pin', href: `https://www.google.com/maps/search/?api=1&query=${ll}` },
  ]
}

/** Rider of the latest out-for-delivery, or null when the courier sent none. */
export function getRider(order) {
  const ofd = order?.milestoneInfo?.outForDelivery
  if (ofd && (ofd.riderName || ofd.riderPhone)) {
    return { name: ofd.riderName || null, phone: ofd.riderPhone || null }
  }
  const fe = order?.feDetails
  if (fe && (fe.name || fe.phone)) {
    return { name: fe.name || null, phone: fe.phone || null }
  }
  return null
}

/** Index of the last scan with this status code (history is oldest-first), or -1. */
export function lastIndexOfStatus(history, code) {
  for (let i = (history?.length || 0) - 1; i >= 0; i--) {
    if (history[i]?.orderStatusCode === code) return i
  }
  return -1
}

/**
 * Location of a Delivered / Failed Delivery scan: the scan's own coordinates first; milestoneInfo only for
 * the latest scan of that status, because milestoneInfo holds nothing about earlier attempts.
 */
export function getScanLocation(item, isLatestOfItsStatus, milestoneInfo) {
  const own = item?.additional
  if (isValidCoord(own?.latitude, own?.longitude)) {
    return { lat: String(own.latitude).trim(), lng: String(own.longitude).trim() }
  }
  if (!isLatestOfItsStatus) return null
  const m = item?.orderStatusCode === DELIVERED_CODE ? milestoneInfo?.delivered
    : item?.orderStatusCode === FAILED_DELIVERY_CODE ? milestoneInfo?.failedDelivery
    : null
  if (m && isValidCoord(m.latitude, m.longitude)) {
    return { lat: String(m.latitude).trim(), lng: String(m.longitude).trim() }
  }
  return null
}
